import React, { useMemo } from 'react';
import FuzzySearch from 'fuzzy-search';
import { useUniqueLabs } from '../../../../../hooks/api/useUniqueLabs';
import * as Card from '../../../../v2BaseComponents/Card';
import { useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import { getIntl } from '../../../../../utils/intl';
import * as Filtering from '../../../../shared/Filtering';
import HeaderContainer from '../../../../v2BaseComponents/HeaderContainer';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';
import { ContentStack } from '../../../../v2BaseComponents/Stack';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import MaxWidthRestrictedContent from '../../../../v2BaseComponents/MaxWidthRestrictedContent';
import { FilterButton } from '../../../../shared/Filtering';
import getDateString from '../../../../../utils/getDateString';
import { PRScrollView } from '../../../../shared/Scroll';

const intl = getIntl('unique-labs', {
    lab: 'Lab',
    placeholder: 'Search Labs',
    sortLabs: 'Sort Labs',
    labName: 'Name',
    aToZ: 'A-Z',
    zToA: 'Z-A',
    labDate: 'Date',
    newest: 'Newest',
    oldest: 'Oldest',
});

export type UniqueLabsOwnProps = {
    medicalProfileId: string;
};

export type UniqueLabsProps = UniqueLabsOwnProps;

type AscendingDescending = 'asc' | 'desc' | null;

type SortState = {
    alphabetically: AscendingDescending;
    date: AscendingDescending;
};

const defaultSortState: SortState = {
    alphabetically: 'asc',
    date: null,
};

const UniqueLabs: React.FC<UniqueLabsProps> = ({ medicalProfileId }) => {
    const [searchText, setSearchText] = React.useState('');
    const [sortState, setSortState] = React.useState<SortState>(defaultSortState);
    const navigation = useProfilesTabNavigation();
    const { isLoadingUniqueLabs, uniqueLabs } = useUniqueLabs({ medicalProfileId });
    const sortedLabs = useMemo(() => {
        if (sortState.date) {
            return uniqueLabs.codes.sort((a, b) =>
                sortState.date === 'asc'
                    ? new Date(b.mostRecentDateTime).getTime() - new Date(a.mostRecentDateTime).getTime()
                    : new Date(a.mostRecentDateTime).getTime() - new Date(b.mostRecentDateTime).getTime(),
            );
        } else {
            return uniqueLabs.codes.sort((a, b) =>
                sortState.alphabetically === 'asc' || sortState.alphabetically === null
                    ? a.coding.display.localeCompare(b.coding.display)
                    : b.coding.display.localeCompare(a.coding.display),
            );
        }
    }, [uniqueLabs, sortState]);
    const searchFilteredLabs = useMemo(() => {
        if (!searchText) {
            return sortedLabs;
        }
        const searcher = new FuzzySearch(sortedLabs, ['coding.display'], {
            caseSensitive: false,
            sort: true,
        });
        return searcher.search(searchText);
    }, [searchText, sortedLabs]);

    return (
        <AnimateLoadedContent isLoading={isLoadingUniqueLabs}>
            {() => (
                <>
                    <HeaderContainer>
                        <Filtering.Container
                            renderSearch={() => (
                                <Filtering.SearchBar
                                    value={searchText}
                                    onChange={setSearchText}
                                    placeholder={intl('placeholder')}
                                    isDisabled={!uniqueLabs.codes.length}
                                />
                            )}
                            renderFilterButton={() => (
                                <FilterButton
                                    buttonIcon="VerticalArrows"
                                    buttonTestID="sort-button"
                                    filterPromptTitle={intl('sortLabs')}
                                    onResetFilters={() => setSortState(defaultSortState)}
                                    popoverTitle={intl('sortLabs')}
                                    renderFilterContents={() => (
                                        <Filtering.FilterPopoverContent
                                            resetDisabled={
                                                sortState.date === defaultSortState.date &&
                                                sortState.alphabetically === defaultSortState.alphabetically
                                            }
                                        >
                                            <Filtering.SelectFilter
                                                onClear={() => setSortState({ ...sortState, date: null })}
                                                title={intl('labName')}
                                                value={sortState.alphabetically}
                                                options={[
                                                    { value: 'asc', label: intl('aToZ') },
                                                    { value: 'desc', label: intl('zToA') },
                                                ]}
                                                onChange={(_) => setSortState({ date: null, alphabetically: _ })}
                                            />
                                            <Filtering.SelectFilter
                                                onClear={() => setSortState({ ...sortState, date: null })}
                                                title={intl('labDate')}
                                                value={sortState.date}
                                                options={[
                                                    { value: 'asc', label: intl('newest') },
                                                    { value: 'desc', label: intl('oldest') },
                                                ]}
                                                onChange={(_) => setSortState({ date: _, alphabetically: null })}
                                            />
                                        </Filtering.FilterPopoverContent>
                                    )}
                                />
                            )}
                        />
                    </HeaderContainer>
                    <MaxWidthRestrictedContent
                        style={{ flex: 1 }}
                        contentContainerStyle={{ flex: 1, alignSelf: 'stretch' }}
                    >
                        <PRScrollView style={{ width: '100%', flex: 1 }}>
                            <MainContentContainer>
                                <ContentStack>
                                    {searchFilteredLabs.map((code, index) => (
                                        <Card.Card
                                            key={code.coding.code + index}
                                            onPress={() => {
                                                navigation.navigate('lab-history', {
                                                    profileId: medicalProfileId,
                                                    labCode: code.coding.code,
                                                });
                                            }}
                                        >
                                            <Card.CardHeader>{code.coding.display}</Card.CardHeader>
                                            <Card.CardChipSet
                                                chips={[
                                                    {
                                                        title: getDateString(code.mostRecentDateTime, 'date-time'),
                                                    },
                                                ]}
                                            />
                                        </Card.Card>
                                    ))}
                                </ContentStack>
                            </MainContentContainer>
                        </PRScrollView>
                    </MaxWidthRestrictedContent>
                </>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(UniqueLabs);
